import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2, Mail, Phone, User, FileText } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { apiFetch } from '@/lib/api';
import { isValidCPF, isValidCNPJ, formatCNPJ } from '@/utils/cpfValidation';
import { EnderecoParceiroForm, type EnderecoParceiroValues } from './EnderecoParceiroForm';

const formatTelefone = (value: string) => {
  value = value.replace(/\D/g, '').slice(0, 11);
  if (value.length === 11) {
    return value.replace(/^(\d{2})(\d{5})(\d{4})$/, '($1) $2-$3');
  }
  if (value.length === 10) {
    return value.replace(/^(\d{2})(\d{4})(\d{4})$/, '($1) $2-$3');
  }
  return value;
};

const formatCPF = (value: string) => {
  value = value.replace(/\D/g, '').slice(0, 11);
  return value.replace(/^(\d{3})(\d{3})(\d{3})(\d{2})$/, '$1.$2.$3-$4');
};

interface CadastroParceiroAuthProps {
  open: boolean;
  onClose: () => void;
  onSuccess: (message: string) => void;
}

const initialEndereco: EnderecoParceiroValues = {
  cep: '',
  endereco: '',
  numero: '',
  bairro: '',
  cidade: '',
  estado: '',
};

type TipoPessoa = 'PF' | 'PJ';

export function CadastroParceiroAuth({ open, onClose, onSuccess }: CadastroParceiroAuthProps) {
  const [tipoPessoa, setTipoPessoa] = useState<TipoPessoa>('PF');
  const [formData, setFormData] = useState({
    nome: '',
    email: '',
    telefone: '',
    cpf: '',
    cnpj: '',
    razao_social: '',
    nome_fantasia: '',
    cpf_representante: ''
  });
  const [endereco, setEndereco] = useState<EnderecoParceiroValues>(initialEndereco);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) {
      setTipoPessoa('PF');
      setFormData({
        nome: '', email: '', telefone: '', cpf: '',
        cnpj: '', razao_social: '', nome_fantasia: '', cpf_representante: ''
      });
      setEndereco(initialEndereco);
      setError(null);
    }
  }, [open]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    const camposEndereco = ['cep', 'endereco', 'numero', 'bairro', 'cidade', 'estado'] as const;
    const faltando = camposEndereco.filter((c) => !endereco[c]?.trim());
    if (faltando.length > 0) {
      setError('Preencha todos os campos do endereço (CEP, rua, número, bairro, cidade e estado).');
      return;
    }

    if (tipoPessoa === 'PF') {
      const cpfDigits = formData.cpf.replace(/\D/g, '');
      if (!cpfDigits || cpfDigits.length !== 11) {
        setError('CPF inválido.');
        return;
      }
      if (!isValidCPF(formData.cpf)) {
        setError('CPF inválido.');
        return;
      }
    } else {
      const cnpjDigits = formData.cnpj.replace(/\D/g, '');
      if (!cnpjDigits || cnpjDigits.length !== 14) {
        setError('CNPJ inválido.');
        return;
      }
      if (!isValidCNPJ(formData.cnpj)) {
        setError('CNPJ inválido.');
        return;
      }
      if (!formData.razao_social?.trim()) {
        setError('Razão social é obrigatória para Pessoa Jurídica.');
        return;
      }
      const cpfRepDigits = formData.cpf_representante.replace(/\D/g, '');
      if (!cpfRepDigits || cpfRepDigits.length !== 11) {
        setError('CPF do representante legal é obrigatório e inválido.');
        return;
      }
      if (!isValidCPF(formData.cpf_representante)) {
        setError('CPF do representante legal inválido.');
        return;
      }
    }

    setLoading(true);
    try {
      const body: Record<string, string> = {
        nome: (tipoPessoa === 'PJ' ? formData.razao_social : formData.nome).trim(),
        email: formData.email.trim(),
        telefone: formData.telefone.replace(/\D/g, ''),
        cidade: endereco.cidade.trim(),
        estado: endereco.estado.trim().toUpperCase(),
        cep: endereco.cep.replace(/\D/g, ''),
        endereco: endereco.endereco.trim(),
        numero: endereco.numero.trim(),
        bairro: endereco.bairro.trim(),
        tipo_pessoa: tipoPessoa
      };
      if (tipoPessoa === 'PF') {
        body.cpf = formData.cpf.replace(/\D/g, '');
      } else {
        body.cnpj = formData.cnpj.replace(/\D/g, '');
        body.razao_social = formData.razao_social.trim();
        body.nome_fantasia = formData.nome_fantasia?.trim() || '';
        body.cpf_representante = formData.cpf_representante.replace(/\D/g, '');
      }
      const res = await apiFetch('/api/cadastros-pendentes', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error || 'Erro ao enviar cadastro.');
        return;
      }
      onSuccess(
        'O cadastro do parceiro será analisado pela equipe técnica da empresa e a aprovação será mediante análise.'
      );
      onClose();
    } catch {
      setError('Erro de conexão. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="sm:max-w-[500px] max-h-[90vh] overflow-y-auto bg-sun7-white text-sun7-blue">
        <DialogHeader>
          <DialogTitle>Cadastre-se como parceiro Sun7</DialogTitle>
          <DialogDescription>
            Preencha os dados abaixo. Sua solicitação será analisada pela equipe Sun7.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label>Tipo de cadastro</Label>
            <Select value={tipoPessoa} onValueChange={(v: TipoPessoa) => setTipoPessoa(v)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="PF">Pessoa Física (CPF)</SelectItem>
                <SelectItem value="PJ">Pessoa Jurídica (CNPJ)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {/* Dados PJ: primeiros campos do formulário (Razão Social, CNPJ, Representante Legal, CPF) */}
          {tipoPessoa === 'PJ' && (
            <>
              <div className="space-y-2">
                <Label htmlFor="auth-parceiro-razao_social" className="flex items-center gap-2">
                  <FileText className="h-4 w-4" />
                  Razão Social *
                </Label>
                <Input
                  id="auth-parceiro-razao_social"
                  value={formData.razao_social}
                  onChange={(e) => setFormData({ ...formData, razao_social: e.target.value })}
                  placeholder="Ex: Solar Tech Ltda"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="auth-parceiro-cnpj">CNPJ *</Label>
                <Input
                  id="auth-parceiro-cnpj"
                  value={formatCNPJ(formData.cnpj)}
                  onChange={(e) => setFormData({ ...formData, cnpj: e.target.value.replace(/\D/g, '') })}
                  placeholder="00.000.000/0000-00"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="auth-parceiro-nome_fantasia">Representante Legal *</Label>
                <Input
                  id="auth-parceiro-nome_fantasia"
                  value={formData.nome_fantasia}
                  onChange={(e) => setFormData({ ...formData, nome_fantasia: e.target.value })}
                  placeholder="Nome do representante legal"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="auth-parceiro-cpf_representante">CPF do Representante Legal *</Label>
                <Input
                  id="auth-parceiro-cpf_representante"
                  value={formatCPF(formData.cpf_representante)}
                  onChange={(e) => setFormData({ ...formData, cpf_representante: e.target.value.replace(/\D/g, '') })}
                  placeholder="000.000.000-00"
                  required
                />
              </div>
            </>
          )}
          {tipoPessoa === 'PF' && (
            <div className="space-y-2">
              <Label htmlFor="auth-parceiro-nome" className="flex items-center gap-2">
                <User className="h-4 w-4" />
                Nome Completo *
              </Label>
              <Input
                id="auth-parceiro-nome"
                value={formData.nome}
                onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                placeholder="Ex: João Silva"
                required
              />
            </div>
          )}
          {tipoPessoa === 'PF' && (
            <div className="space-y-2">
              <Label htmlFor="auth-parceiro-cpf" className="flex items-center gap-2">
                <FileText className="h-4 w-4" />
                CPF *
              </Label>
              <Input
                id="auth-parceiro-cpf"
                value={formatCPF(formData.cpf)}
                onChange={(e) => setFormData({ ...formData, cpf: e.target.value.replace(/\D/g, '') })}
                placeholder="000.000.000-00"
                required
              />
            </div>
          )}
          <div className="space-y-2">
            <Label htmlFor="auth-parceiro-email" className="flex items-center gap-2">
              <Mail className="h-4 w-4" />
              Email *
            </Label>
            <Input
              id="auth-parceiro-email"
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              placeholder="email@exemplo.com"
              required
            />
            <p className="text-xs text-muted-foreground">Será usado para login no sistema</p>
          </div>
          <div className="space-y-2">
            <Label htmlFor="auth-parceiro-telefone" className="flex items-center gap-2">
              <Phone className="h-4 w-4" />
              Telefone *
            </Label>
            <Input
              id="auth-parceiro-telefone"
              value={formatTelefone(formData.telefone)}
              onChange={(e) => setFormData({ ...formData, telefone: e.target.value.replace(/\D/g, '') })}
              placeholder="(00) 00000-0000"
              required
            />
          </div>

          {/* Endereço completo com CEP */}
          <div>
            <h3 className="text-sm font-medium mb-2">Endereço *</h3>
            <EnderecoParceiroForm
              values={endereco}
              onChange={setEndereco}
              disabled={loading}
              idPrefix="auth-parceiro"
            />
          </div>

          {error && (
            <p className="text-sm text-destructive font-medium">{error}</p>
          )}
          <div className="flex justify-end gap-2 pt-4">
            <Button type="button" variant="outline" onClick={onClose} disabled={loading}>
              Cancelar
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Enviando...
                </>
              ) : (
                'Enviar solicitação'
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
